import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, MicOff, X } from 'lucide-react';
import { CameraIntercomSession, IntercomState } from '../../services/camera-intercom.service';
import './CameraIntercomControl.css';

/** Read camera capabilities, rather than assuming an NVR's talk support applies to every input. */
export function cameraIntercomSupport(camera: { cloud_provider?: string; capabilities?: unknown }): boolean | undefined {
  if (camera.cloud_provider?.toLowerCase() !== 'hikvision') return false;
  let capabilities = camera.capabilities;
  if (typeof capabilities === 'string') {
    try { capabilities = JSON.parse(capabilities); } catch { return undefined; }
  }
  const value = (capabilities as { supports_two_way_audio?: unknown } | null)?.supports_two_way_audio;
  if (value === false || value === 0 || value === '0' || value === 'false') return false;
  if (value === true || value === 1 || value === '1' || value === 'true') return true;
  return undefined;
}

export default function CameraIntercomControl({ cameraId, cameraName, supported, size = 'sm', onConnectionChange }: {
  cameraId: number; cameraName?: string; supported?: boolean; size?: 'sm' | 'md' | 'lg'; onConnectionChange?: (active: boolean) => void;
}) {
  const [state, setState] = useState<IntercomState>({ phase:'idle' });
  const session = useRef<CameraIntercomSession | null>(null);
  const mounted = useRef(false);
  const change = useRef(onConnectionChange); change.current = onConnectionChange;
  useEffect(() => {
    mounted.current = true;
    setState({ phase:'idle' });
    return () => {
      mounted.current = false;
      void session.current?.stop();
      session.current = null;
      change.current?.(false);
    };
  }, [cameraId, supported]);
  const connected = state.phase === 'active';
  const connecting = state.phase === 'connecting';
  const busy = connecting || state.phase === 'stopping';
  const unavailable = supported === false;
  const label = unavailable ? 'Intercom unavailable' : connecting ? 'Cancel intercom connection' : connected ? 'Stop intercom' : 'Start intercom';
  const toggle = () => {
    if (unavailable) return;
    if (connecting || connected) { void session.current?.stop(); return; }
    const next = new CameraIntercomSession(cameraId, result => {
      if (!mounted.current || session.current !== next) return;
      setState(result);
      change.current?.(result.phase === 'connecting' || result.phase === 'active' || result.phase === 'stopping');
    });
    session.current = next;
    void next.start();
  };
  const Icon = busy ? Loader2 : connected ? Mic : MicOff;
  return <div className="camera-intercom" data-active={connected} data-unavailable={unavailable}>
    <button type="button" className="camera-intercom__button" aria-label={label} aria-pressed={connected}
      title={`${unavailable ? 'Two-way audio is not available for this camera' : label}${cameraName ? ` · ${cameraName}` : ''}`} disabled={unavailable || state.phase === 'stopping'}
      onClick={event => { event.stopPropagation(); toggle(); }}>
      <Icon size={size === 'sm' ? 16 : size === 'md' ? 20 : 24} className={busy ? 'animate-spin' : undefined} aria-hidden="true" />
    </button>
    {connecting && <span className="camera-intercom__status" role="status" title={state.detail}>{state.detail || 'Connecting intercom'}</span>}
    {connected && <span className="camera-intercom__status" role="status">Microphone on</span>}
    {state.error && <div className="camera-intercom__error" role="alert" onClick={event => event.stopPropagation()}>
      <span>{state.error}</span><button type="button" aria-label="Dismiss intercom error" onClick={() => setState({ phase:'idle' })}><X size={14} /></button>
    </div>}
  </div>;
}
